import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormGroup, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';

@Component({
  selector: 'app-signup-step-three',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  template: `
    <div [formGroup]="formGroup">
      <div class="form-group mb-3">
        <label for="password" class="form-label">Password</label>
        <div class="password-input-wrapper">
          <input 
            [type]="showPassword ? 'text' : 'password'" 
            class="form-control" 
            id="password" 
            formControlName="password"
            placeholder="Create a password"
            [class.is-invalid]="formGroup.get('password')?.invalid && formGroup.get('password')?.touched"
          />
          <button 
            type="button" 
            class="password-toggle"
            (click)="showPassword = !showPassword"
          >
            <i [class]="showPassword ? 'fa fa-eye-slash' : 'fa fa-eye'"></i>
          </button>
        </div>
        <div class="invalid-feedback" *ngIf="formGroup.get('password')?.invalid && formGroup.get('password')?.touched">
          <span *ngIf="formGroup.get('password')?.errors?.['required']">Password is required</span>
          <span *ngIf="formGroup.get('password')?.errors?.['minlength']">Password must be at least 6 characters</span>
        </div>
        <div class="password-hint" *ngIf="!formGroup.get('password')?.touched">
          Password must be at least 6 characters long
        </div>
      </div>

      <div class="form-group mb-3">
        <label for="confirmPassword" class="form-label">Confirm Password</label>
        <div class="password-input-wrapper">
          <input 
            [type]="showConfirmPassword ? 'text' : 'password'" 
            class="form-control" 
            id="confirmPassword" 
            formControlName="confirmPassword"
            placeholder="Re-enter your password"
            [class.is-invalid]="formGroup.get('confirmPassword')?.invalid && formGroup.get('confirmPassword')?.touched"
          />
          <button 
            type="button" 
            class="password-toggle"
            (click)="showConfirmPassword = !showConfirmPassword"
          >
            <i [class]="showConfirmPassword ? 'fa fa-eye-slash' : 'fa fa-eye'"></i>
          </button>
        </div>
        <div class="invalid-feedback" *ngIf="formGroup.get('confirmPassword')?.invalid && formGroup.get('confirmPassword')?.touched">
          <span *ngIf="formGroup.get('confirmPassword')?.errors?.['required']">Please confirm your password</span>
        </div>
        <div class="invalid-feedback" *ngIf="formGroup.errors?.['passwordMismatch'] && formGroup.get('confirmPassword')?.touched">
          Passwords do not match
        </div>
      </div>

      <div class="form-group mb-4">
        <div class="form-check">
          <input 
            type="checkbox" 
            class="form-check-input" 
            id="termsAccepted" 
            formControlName="termsAccepted"
          />
          <label class="form-check-label" for="termsAccepted">
            I agree to all <a href="https://shiftclockai.com/terms.html" target="_blank" class="text-primary">Terms and Conditions</a>
          </label>
        </div>
        <div class="invalid-feedback d-block" *ngIf="formGroup.get('termsAccepted')?.invalid && formGroup.get('termsAccepted')?.touched">
          You must accept the terms and conditions
        </div>
      </div>

      <div class="button-group">
        <button 
          type="button" 
          class="btn btn-outline-secondary"
          (click)="onBack()"
        >
          Back
        </button>
        <button 
          type="button" 
          class="btn btn-primary"
          (click)="onSubmit()"
          [disabled]="!isStepValid() || isLoading"
        >
          <span *ngIf="!isLoading">Create An Account</span>
          <span *ngIf="isLoading">
            <i class="fa fa-spinner fa-spin"></i> Creating...
          </span>
        </button>
      </div>
    </div>
  `,
  styles: [`
    .form-control {
      padding: 12px 16px;
      border-radius: 8px;
      border: 1px solid #e0e0e0;
      font-size: 14px;
    }

    .form-control:focus {
      border-color: #6a49f2;
      box-shadow: 0 0 0 0.2rem rgba(106, 73, 242, 0.15);
    }

    .form-label {
      font-weight: 500;
      margin-bottom: 8px;
      color: #3e3f66;
    }

    .password-input-wrapper {
      position: relative;
    }

    .password-toggle {
      position: absolute;
      right: 12px;
      top: 50%;
      transform: translateY(-50%);
      background: none;
      border: none;
      color: #6c757d;
      cursor: pointer;
      padding: 4px 8px;
    }

    .password-toggle:hover {
      color: #6a49f2;
    }

    .password-hint {
      font-size: 12px;
      color: #6c757d;
      margin-top: 4px;
    }

    .form-check {
      display: flex;
      align-items: flex-start;
      gap: 8px;
    }

    .form-check-input {
      width: 18px;
      height: 18px;
      margin-top: 2px;
      cursor: pointer;
    }

    .form-check-input:checked {
      background-color: #6a49f2;
      border-color: #6a49f2;
    }

    .form-check-label {
      font-size: 14px;
      color: #3e3f66;
      cursor: pointer;
    }

    .text-primary {
      color: #6a49f2;
      text-decoration: none;
      font-weight: 500;
    }

    .text-primary:hover {
      text-decoration: underline;
    }

    .button-group {
      display: flex;
      gap: 12px;
    }

    .btn {
      flex: 1;
      padding: 12px;
      border-radius: 8px;
      font-weight: 600;
      transition: all 0.3s;
      border: none;
    }

    .btn-primary {
      background: #6a49f2;
    }

    .btn-primary:hover:not(:disabled) {
      background: #5639d9;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(106, 73, 242, 0.3);
    }

    .btn-primary:disabled {
      background: #ccc;
      cursor: not-allowed;
    }

    .btn-outline-secondary {
      background: white;
      border: 2px solid #e0e0e0;
      color: #3e3f66;
    }

    .btn-outline-secondary:hover {
      background: #f5f5f5;
      border-color: #d0d0d0;
      transform: translateY(-2px);
    }

    .is-invalid {
      border-color: #dc3545;
    }

    .invalid-feedback {
      display: block;
      color: #dc3545;
      font-size: 12px;
      margin-top: 4px;
    }
  `]
})
export class SignupStepThreeComponent {
  @Input() formGroup!: FormGroup;
  @Input() isLoading: boolean = false;
  @Output() submit = new EventEmitter<void>();
  @Output() previousStep = new EventEmitter<void>();

  showPassword: boolean = false;
  showConfirmPassword: boolean = false;

  isStepValid(): boolean {
    const password = this.formGroup.get('password');
    const confirmPassword = this.formGroup.get('confirmPassword');
    const termsAccepted = this.formGroup.get('termsAccepted');
    return !!(password?.valid && confirmPassword?.valid && termsAccepted?.valid && !this.formGroup.errors?.['passwordMismatch']);
  }

  onSubmit(): void {
    if (this.isStepValid()) {
      this.submit.emit();
    }
  }

  onBack(): void {
    this.previousStep.emit();
  }
}
